import React from 'react'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { TrendingUp } from 'lucide-react'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { decimalValue } from '../../core/helper/date-format'
import { useNavigate } from 'react-router-dom'
import CustomChip from '../../Component/CustomChip/CustomChip'

const Section = styled('section')(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#0E1116',
  color: '#fff',
  padding: '120px 0 80px',
  overflow: 'hidden',
  backgroundImage:
    'radial-gradient(800px 400px at 70% 20%, rgba(29, 176, 161, 0.08) 0%, rgba(14,17,22,0) 60%), radial-gradient(600px 300px at 10% 60%, rgba(29, 176, 161, 0.08) 0%, rgba(14,17,22,0) 60%)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
    backgroundSize: '60px 60px',
    opacity: 0.25,
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('md')]: {
    padding: '100px 0 60px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '80px 0 40px',
  },
}))

const Trusted = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 14px',
  borderRadius: '999px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#B1B5C3',
  fontWeight: 600,
  fontSize: '0.95rem',
  margin: '0 auto 20px',
  '@media (max-width:600px)': {
    fontSize: '0.75rem',
    padding: '6px 12px',
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  textAlign: 'center',
  fontSize: '4.4rem',
  lineHeight: 1.1,
  letterSpacing: '-0.5px',
  // marginBottom: '12px',
  '& .accent': {
    color: '#1DB0A1',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '3.2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.4rem',
  },
}))

const Subtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: '#9AA6B2',
  fontSize: '1.05rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    padding: '0 16px',
  },
}))

const Actions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '14px',
  justifyContent: 'center',
  marginTop: '40px',
  marginBottom: '20px',
  flexWrap: 'wrap',
}))

const PrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '12px',
  padding: '12px 48px',
  fontWeight: 700,
  backgroundColor: '#1DAFA1',
  color: 'white',
  '&:hover': {
    backgroundColor: '#189a8dff',
  },
  '@media (max-width:600px)': {
    padding: '10px 36px'
  },
}))

const SecondaryButton = styled(Button)(({ theme }) => ({
  // marginTop: '24px',
  textTransform: 'none',
  borderRadius: '12px',
  padding: '12px 40px',
  fontSize: '1rem',
  fontWeight: 500,
  backgroundColor: 'transparent',
  color: '#fff',
  border: '1px solid #23262F',
  '&:hover': {
    backgroundColor: '#21242C',
    borderColor: '#21AB9D',
    '& .MuiSvgIcon-root': {
      transform: 'translateX(5px)',
    },
  },
  '& .MuiSvgIcon-root': {
    transition: 'transform 0.3s ease',
  },
  '@media (max-width:600px)': {
    padding: '10px 28px'
  },
}))

const Stage = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: '6px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '420px',
  [theme.breakpoints.down('sm')]: {
    minHeight: '340px',
  },
}))

const Phone = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 220,
  height: 445,
  padding: '6px',
  background: 'linear-gradient(180deg, #1E232A 0%, #161B22 100%)',
  borderRadius: 40,
  margin: '30px auto',
  boxShadow:
    '0 24px 60px rgba(0,0,0,0.4), 0 20px 60px -10px rgba(29,175,161,0.25)',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  willChange: 'transform',
  animation: 'floatY 10s ease-in-out infinite',
  '@keyframes floatY': {
    '0%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(20px)' },
    '100%': { transform: 'translateY(0)' },
  },
  transition: 'transform 0.5s ease',
  '&:hover': {
    transform: 'translateY(-10px) rotateX(5deg) rotateY(-5deg)',
    animationPlayState: 'paused',
  },
  [theme.breakpoints.up('sm')]: {
    width: 240,
    height: 460,
  },
}))

const Screen = styled(Box)({
  flex: 1,
  background: '#0B0E14',
  position: 'relative',
  margin: 0,
  padding: '12px',
  borderRadius: '28px',
})

const Pills = styled(Box)({
  display: 'flex',
  gap: '8px',
})

const Pill = styled(Box)({
  padding: '6px 10px',
  borderRadius: '999px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  color: '#9AA6B2',
  fontWeight: 500,
  fontSize: '0.7rem',
})

const PillActive = styled(Pill)({
  color: '#fff',
  backgroundColor: 'rgba(33,202,80,0.18)',
})

const Amount = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.3rem',
  color: '#fff',
  padding: '8px 0px',
}))

const Chart = styled(Box)({
  marginTop: '8px',
  height: '160px',
  borderRadius: '12px',
  background:
    'linear-gradient(180deg, rgba(43,216,163,0.12) 0%, rgba(43,216,163,0.0) 100%)',
  position: 'relative',
  overflow: 'hidden',
})

const ChartSVG = styled('svg')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
})

const PriceDot = styled(Box)({
  position: 'absolute',
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: '#2BD8A3',
  boxShadow: '0 0 12px rgba(43,216,163,0.6)',
})

const PriceTag = styled(Box)({
  position: 'absolute',
  padding: '6px 10px',
  borderRadius: '8px',
  backgroundColor: 'rgba(33,202,80,0.18)',
  color: '#fff',
  border: '1px solid rgba(33,202,80,0.6)',
  fontWeight: 700,
})

const Floating = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

const FloatingAnim = styled(Floating)(({ theme }) => ({
  willChange: 'transform',
  animation: 'floatY 10s ease-in-out infinite',
  '@keyframes floatY': {
    '0%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(20px)' },
    '100%': { transform: 'translateY(0)' },
  },
}))

const Card = styled(Box)({
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(6px)',
  color: '#fff',
  borderRadius: '16px',
  padding: '14px',
  minWidth: '180px',
})

const Muted = styled(Typography)({
  color: '#9AA6B2',
})

const ValueLarge = styled(Typography)({
  fontWeight: 500,
  fontSize: '1.2rem',
  color: '#fff',
})

const ChangeText = styled(Typography)(({ theme, color = '#21CA50' }) => ({
  fontWeight: 400,
  color: color || '#21CA50',
}))

const PurplePin = styled(Box)({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  bottom: -24,
  width: 32,
  height: 32,
  borderRadius: '50%',
  background:
    'radial-gradient(60% 60% at 50% 50%, rgba(151,71,255,0.6) 0%, rgba(151,71,255,0.2) 60%, rgba(151,71,255,0) 100%)',
  border: '1px solid rgba(151,71,255,0.4)',
})

const BarsMini = styled(Box)(({ color = '#1B716B' }) => ({
  display: 'flex',
  gap: '6px',
  marginTop: '8px',
  '& > span': {
    display: 'block',
    width: '6px',
    borderRadius: '6px',
    backgroundColor: color || '#1B716B',
    opacity: 0.9,
  },
}))

const Notch = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 6,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 88,
  height: 16,
  borderRadius: '0 0 14px 14px',
  backgroundColor: '#1E232A',
  boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.04)',
  zIndex: 999,
}))

const Glow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  width: '60%',
  height: 24,
  background:
    'radial-gradient(50% 100% at 50% 100%, rgba(33,202,80,0.28) 0%, rgba(33,202,80,0) 70%)',
  filter: 'blur(14px)',
  zIndex: 0,
}))

const Bottom = styled(Box)(({ theme }) => ({
  marginTop: '40px',
  paddingTop: '24px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
}))

const StatGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '32px',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: '16px',
}))

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '140px',
}))

const StatValue = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.6rem',
  color: '#fff',
}))

const StatLabel = styled(Typography)(({ theme }) => ({
  color: '#9AA6B2',
}))

const TickerViewport = styled(Box)(({ theme }) => ({
  backgroundColor: '#0C0E13',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  borderTop: '1px solid rgba(255,255,255,0.12)',
  // borderRadius: '16px',
  padding: '12px',
  overflow: 'hidden',
}))

const TickerTrack = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  whiteSpace: 'nowrap',
  animation: 'ticker-move 40s linear infinite',
  '@keyframes ticker-move': {
    '0%': { transform: 'translateX(0%)' },
    '100%': { transform: 'translateX(-50%)' },
  },
  [theme.breakpoints.down('md')]: {
    animationDuration: '50s',
  },
  [theme.breakpoints.down('sm')]: {
    animationDuration: '60s',
  },
}))

const TickerItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '9px',
  padding: '10px 14px',
}))

const TokenPair = styled(Typography)({
  fontWeight: 700,
})

const TokenPrice = styled(Typography)({
  color: '#fff',
})

const TokenChange = styled(Typography)(({ theme, color }) => ({
  fontWeight: 700,
  color,
}))

const Bars = styled(Box)(({ color }) => ({
  display: 'flex',
  gap: '3px',
  marginLeft: '4px',
  '& > span': {
    display: 'block',
    width: '4px',
    borderRadius: '2px',
    backgroundColor: color,
    opacity: 0.4,
  },
}))

function HeroSection({ marketCurrency, marketValues, usdtValues }) {
  const navigate = useNavigate()
  return (
    <Section>
      <div>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: { xs: 3, md: 0 },
            px: { xs: 1, sm: 2, md: 0 },
          }}
        >
          <CustomChip>
            <Box
              sx={{
                bgcolor: '#1DB0A1',
                height: 6,
                width: 6,
                borderRadius: 20,
              }}
            />
            Trusted by professional traders
          </CustomChip>
          <Title component='h1'>
            Trade Crypto{' '}
            <span
              style={{
                display: 'inline-block',
                backgroundImage: 'linear-gradient(to right, #21AB9D, #34d399)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Confidently.
            </span>
            <br />
            Build Wealth Without <br /> Borders.
          </Title>
          <Subtitle>
            Spot, Futures, OTC & Earn — all in one secure global crypto exchange
            trusted by traders worldwide.
          </Subtitle>
          <Actions>
            <PrimaryButton onClick={() => navigate('/register')}>
              Get Started Free →
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/markets')}>
              Explore Markets <KeyboardArrowRightIcon />
            </SecondaryButton>
          </Actions>
        </Box>
        <Stage>
          <FloatingAnim
            sx={{
              left: { xs: '0', sm: '6%', md: '17%', lg: '20%' },
              top: '32%',
            }}
          >
            <Card>
              <Muted variant='body2'>BTC</Muted>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ValueLarge>{usdtValues?.['BTC_USDT']?.last}</ValueLarge>
                {Number(usdtValues?.['BTC_USDT']?.details?.changeRate) > 0 ? (
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                    }}
                    htmlColor={'#21CA50'}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{
                      fontSize: 16,
                    }}
                    htmlColor={'#FF5A5A'}
                  />
                )}
                <ChangeText
                  variant='body2'
                  color={
                    Number(usdtValues?.['BTC_USDT']?.details?.changeRate) > 0
                      ? '#21CA50'
                      : '#FF5A5A'
                  }
                >
                  {usdtValues?.['BTC_USDT']?.details?.changeRate}%
                </ChangeText>
              </Box>
              <BarsMini
                color={
                  Number(usdtValues?.['BTC_USDT']?.details?.changeRate) > 0
                    ? '#21CA50'
                    : '#FF5A5A'
                }
              >
                <span style={{ height: 8 }} />
                <span style={{ height: 10 }} />
                <span style={{ height: 12 }} />
                <span style={{ height: 14 }} />
                <span style={{ height: 18 }} />
                <span style={{ height: 16 }} />
                <span style={{ height: 14 }} />
                <span style={{ height: 16 }} />
                <span style={{ height: 18 }} />
                <span style={{ height: 16 }} />
              </BarsMini>
            </Card>
            <PurplePin />
          </FloatingAnim>
          <FloatingAnim
            sx={{ right: { xs: '0', sm: '6%', md: '17%' }, top: '20%' }}
          >
            <Card>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flex: '0 0 auto',
                  }}
                >
                  <img
                    src={usdtValues?.['USDT_INR']?.fromCurrency?.image}
                    alt="USDT"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>

                <ValueLarge sx={{ fontSize: '1.1rem' }}>USDT</ValueLarge>
              </Box>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                {Number(usdtValues?.['USDT_INR']?.price)}
              </Typography>
              {/* <ChangeText color={usdtValues?.['USDT_INR']?.change > 0
                ? '#21CA50'
                : '#FF5A5A'} variant='body2'>{(usdtValues?.['USDT_INR']?.change > 0 ? '+' : '') + decimalValue(usdtValues?.['USDT_INR']?.change, 2)}%</ChangeText> */}
              <Typography
                variant='body2'
                sx={{
                  color:
                    usdtValues?.['USDT_INR']?.volume > 0
                      ? '#21CA50'
                      : '#FF5A5A',
                  fontWeight: 700,
                }}
              >
                {decimalValue(usdtValues?.['USDT_INR']?.volume, 2)}
              </Typography>
              {/* <PairMuted variant='body2'>Bitcoin / BTC</PairMuted>
              <BalanceText variant='body2'>Balance: 2.75039 USD</BalanceText> */}
            </Card>
            <PurplePin />
          </FloatingAnim>
          <FloatingAnim
            sx={{ right: { xs: '0', sm: '5%', md: '16%' }, bottom: '6%' }}
          >
            <Card>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flex: '0 0 auto',
                  }}
                >
                  <img
                    src={usdtValues?.['SOL_USDT']?.fromCurrency?.image}
                    alt='SOL'
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
                <Muted variant='body2'>Solana</Muted>
              </Box>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                {Number(usdtValues?.['SOL_USDT']?.details?.last)}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color:
                    Number(usdtValues?.['SOL_USDT']?.details?.vol) > 0
                      ? '#21CA50'
                      : '#FF5A5A',
                  fontWeight: 700,
                }}
              >
                {decimalValue(Number(usdtValues?.['SOL_USDT']?.details?.vol), 2)}
              </Typography>
            </Card>
          </FloatingAnim>
          <Phone>
            <Notch />
            <Screen>
              {/* Status Bar */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  pt: 2,
                  pb: 0.5,
                  fontSize: '9px',
                  color: '#9AA6B2',
                  fontWeight: 500,
                }}
              >
                <span>9:41</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box
                    sx={{
                      width: '14px',
                      height: '7px',
                      border: '1px solid rgba(29,160,148,0.5)',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: '66%',
                        height: '100%',
                        background: '#1DAFA1',
                        borderRadius: '1px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ px: 0, py: 0.8, flex: 1, overflow: 'hidden' }}>
                {/* Logo */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mb: 1,
                  }}
                >
                  <img
                    src='/unitic-logo.png'
                    alt='Unitic'
                    style={{ width: 16, height: 16, objectFit: 'contain' }}
                  />
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      color: '#e6edf3',
                    }}
                  >
                    UNITIC
                  </span>
                </Box>

                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        fontSize: '8px',
                        color: '#9AA6B2',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Portfolio
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'inherit',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        lineHeight: 1.1,
                        background:
                          'linear-gradient(90deg, #1DAFA1 0%, #34d399 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      $148,650
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'rgba(34,197,94,0.15)',
                      color: '#21CA50',
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '999px',
                      fontSize: '8px',
                      fontWeight: 800,
                    }}
                  >
                    <TrendingUp size={10} className='w-2.5 h-2.5' /> +2.8%
                  </Box>
                </Box>

                {/* Chart */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 96, sm: 112 },
                    mb: 1,
                    borderRadius: '12px',
                    background:
                      'linear-gradient(180deg, rgba(29,175,161,0.08) 0%, rgba(29,175,161,0) 100%)',
                    p: '3px',
                  }}
                >
                  <svg
                    viewBox='0 0 240 100'
                    preserveAspectRatio='none'
                    width='100%'
                    height='100%'
                  >
                    <defs>
                      <linearGradient
                        id='heroChartFill'
                        x1='0%'
                        y1='0%'
                        x2='0%'
                        y2='100%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#1DAFA1'
                          stopOpacity='0.35'
                        />
                        <stop
                          offset='100%'
                          stopColor='#1DAFA1'
                          stopOpacity='0'
                        />
                      </linearGradient>
                      <linearGradient
                        id='heroChartLine'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#1DAFA1'
                          stopOpacity='0.5'
                        />
                        <stop
                          offset='50%'
                          stopColor='#1DAFA1'
                          stopOpacity='1'
                        />
                        <stop
                          offset='100%'
                          stopColor='hsl(145,72%,45%)'
                          stopOpacity='1'
                        />
                      </linearGradient>
                    </defs>
                    <path
                      d='M0,75 C20,72 30,68 45,55 C60,42 70,48 90,38 C110,28 120,35 140,25 C160,15 170,20 190,12 C210,5 230,8 240,5'
                      fill='none'
                      stroke='url(#heroChartLine)'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                    />
                    <path
                      d='M0,75 C20,72 30,68 45,55 C60,42 70,48 90,38 C110,28 120,35 140,25 C160,15 170,20 190,12 C210,5 230,8 240,5 L240,100 L0,100 Z'
                      fill='url(#heroChartFill)'
                    />
                    <circle cx='190' cy='12' r='4' fill='#1DAFA1' />
                    <circle
                      cx='190'
                      cy='12'
                      r='8'
                      fill='#1DAFA1'
                      opacity='0.2'
                    />
                  </svg>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 8,
                      background: 'rgba(29,175,161,0.2)',
                      backdropFilter: 'blur(6px)',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '9px',
                      fontWeight: 800,
                      color: '#1DAFA1',
                      border: '1px solid rgba(29,175,161,0.3)',
                    }}
                  >
                    $43,210
                  </Box>
                </Box>

                {/* Quick Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '6px',
                    mb: 0.9,
                  }}
                >
                  {[
                    {
                      label: 'Buy',
                      bg: 'linear-gradient(180deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.05) 100%)',
                      color: '#34D399',
                      border: '1px solid rgba(34,197,94,0.2)',
                    },
                    {
                      label: 'Sell',
                      bg: 'linear-gradient(180deg, rgba(248,113,113,0.2) 0%, rgba(248,113,113,0.05) 100%)',
                      color: '#F87171',
                      border: '1px solid rgba(248,113,113,0.2)',
                    },
                    {
                      label: 'Swap',
                      bg: 'linear-gradient(180deg, rgba(29,175,161,0.2) 0%, rgba(29,175,161,0.05) 100%)',
                      color: '#1DAFA1',
                      border: '1px solid rgba(29,175,161,0.2)',
                    },
                  ].map((a) => (
                    <Box
                      key={a.label}
                      sx={{
                        flex: 1,
                        textAlign: 'center',
                        py: 0.6,
                        borderRadius: '10px',
                        fontSize: '9px',
                        fontWeight: 800,
                        background: a.bg,
                        color: a.color,
                        border: a.border,
                      }}
                    >
                      {a.label}
                    </Box>
                  ))}
                </Box>

                {/* Mini Asset List */}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                >
                  {[
                    {
                      icon: '₿',
                      name: 'BTC',
                      price: '$43,210',
                      change: '+2.3%',
                      up: true,
                    },
                    {
                      icon: 'Ξ',
                      name: 'ETH',
                      price: '$2,310',
                      change: '+1.1%',
                      up: true,
                    },
                    {
                      icon: '◎',
                      name: 'SOL',
                      price: '$98.20',
                      change: '-0.8%',
                      up: false,
                    },
                  ].map((c) => (
                    <Box
                      key={c.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: '5px',
                        borderRadius: '10px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 800,
                            background:
                              'linear-gradient(135deg, rgba(29,175,161,0.25) 0%, rgba(29,175,161,0.1) 100%)',
                            color: '#1DAFA1',
                            border: '1px solid rgba(29,175,161,0.2)',
                          }}
                        >
                          {c.icon}
                        </Box>
                        <Box>
                          <Box
                            sx={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: '#e6edf3',
                            }}
                          >
                            {c.name}
                          </Box>
                          <Box sx={{ fontSize: 8, color: '#9AA6B2' }}>
                            {c.price}
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: c.up ? '#34D399' : '#F87171',
                        }}
                      >
                        {c.change}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Bottom Nav */}
              <Box
                sx={{
                  px: 1.2,
                  py: 1,
                  background: 'transparent',
                  borderTop: '  1px solid rgba(255, 255, 255, 0.02)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  {['Home', 'Trade', 'Earn', 'Wallet'].map((tab, i) => (
                    <Box
                      key={tab}
                      sx={{
                        textAlign: 'center',
                        fontSize: 8,
                        fontWeight: 700,
                        color: i === 0 ? '#1DAFA1' : '#9AA6B2',
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          margin: '0 auto 4px',
                          background: i === 0 ? '#1DAFA1' : 'transparent',
                        }}
                      />
                      {tab}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Screen>
          </Phone>
          <Glow />
        </Stage>
        <Bottom>
          <StatGrid>
            <StatItem>
              <StatValue>24*7</StatValue>
              <StatLabel>Support</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>300+</StatValue>
              <StatLabel>Spot Pairs</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>500+</StatValue>
              <StatLabel>Futures Pairs</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>99.9%</StatValue>
              <StatLabel>Uptime</StatLabel>
            </StatItem>
          </StatGrid>
          <TickerViewport>
            <TickerTrack>
              {[
                ...(marketValues[marketCurrency] || [])
                  .filter(Boolean)
                  .slice(0, 10),
                ...(marketValues[marketCurrency] || [])
                  .filter(Boolean)
                  .slice(0, 10),
              ].map((item, idx) => {
                if (!item || item == null) {
                  return null
                }
                const pair = String(item?.pair || '').toUpperCase()
                const parts = pair.split('_')
                const base = parts[0] || ''
                const quote = parts[1] || ''
                const badgeText = base.slice(0, 2)
                const badgeColors = {
                  BTC: '#F7931A',
                  ETH: '#627EEA',
                  SOL: '#7A52FF',
                  BNB: '#F0B90B',
                  XRP: '#346AA9',
                  ADA: '#3CC8C8',
                }
                const badgeBg = badgeColors[base] || '#132E2F'
                const price = item?.lastPrice ?? item?.price ?? 0
                const change = Number(item?.change ?? 0)
                const isUp = change >= 0
                const changeColor = isUp ? '#21CA50' : '#FF5A5A'
                const currencySymbol = marketCurrency === 'INR' ? '₹' : ''
                const displayPrice =
                  currencySymbol +
                  Number(price).toLocaleString(undefined, {
                    minimumFractionDigits: Number(price) < 1 ? 4 : 2,
                    maximumFractionDigits: Number(price) < 1 ? 4 : 2,
                  })
                const displayChange = `${isUp ? '+' : ''}${change.toFixed(2)}%`
                const heights = isUp ? [8, 10, 7, 11, 9] : [9, 6, 7, 5, 6]
                return (
                  <TickerItem key={idx}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: '0.8rem',
                      }}
                    >
                      <img
                        src={item?.fromCurrency?.image}
                        alt='currencyImage'
                        width='100%'
                        height='100%'
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                    <TokenPair variant='body2'>
                      {base}/{quote}
                    </TokenPair>
                    <TokenPrice variant='body2'>{displayPrice}</TokenPrice>
                    <TokenChange variant='body2' color={changeColor}>
                      {displayChange}
                    </TokenChange>
                    <Bars color={changeColor}>
                      <span style={{ height: heights[0] }} />
                      <span style={{ height: heights[1] }} />
                      <span style={{ height: heights[2] }} />
                      <span style={{ height: heights[3] }} />
                      <span style={{ height: heights[4] }} />
                    </Bars>
                  </TickerItem>
                )
              })}
            </TickerTrack>
          </TickerViewport>
        </Bottom>
      </div>
    </Section>
  )
}

export default HeroSection;